import React, { useState, useEffect } from 'react';

const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const targetDate = new Date('September 13, 2023 14:00:00');
      const distance = targetDate - now;
      
      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00'
        });
        return;
      }
      
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const TimeUnit = ({ value, label, delay }) => (
    <div 
      className={`relative group transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-90'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Main Card */}
      <div className={`
        relative bg-gradient-to-br from-gray-900/80 to-gray-800/90 backdrop-blur-xl 
        p-4 rounded-2xl border border-gray-700/50 shadow-2xl 
        transform transition-all duration-500 group-hover:scale-110 group-hover:shadow-2xl
        ${isHovered ? 'shadow-yellow-500/20' : 'shadow-black/30'}
        min-w-[90px] text-center overflow-hidden
      `}>
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        {/* Shine Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        
        {/* Value */}
        <div className="relative z-10">
          <span className={`
            text-3xl md:text-4xl font-bold bg-gradient-to-br from-yellow-400 to-amber-500 
            bg-clip-text text-transparent transition-all duration-500
            ${isHovered ? 'drop-shadow-lg' : ''}
          `}>
            {value.toString().padStart(2, '0')}
          </span>
          
          {/* Pulsing Dot */}
          <div className="absolute -top-1 -right-1 w-3 h-3">
            <div className="w-full h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full animate-ping opacity-75"></div>
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full"></div>
          </div>
        </div>
        
        {/* Label */}
        <p className={`
          text-xs font-semibold mt-2 transition-all duration-500
          ${isHovered ? 'text-yellow-400' : 'text-gray-400'}
          uppercase tracking-wider
        `}>
          {label}
        </p>
      </div>
      
      {/* Floating Particles */}
      <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400/30 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-float"></div>
      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-amber-400/40 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-float animation-delay-700"></div>
    </div>
  );

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Glow */}
      <div className={`
        absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 
        rounded-3xl blur-xl transition-all duration-1000
        ${isHovered ? 'opacity-50 scale-105' : 'opacity-30 scale-100'}
      `}></div>
      
      {/* Main Container */}
      <div className={`
        relative bg-gradient-to-br from-gray-900/60 to-gray-800/70 backdrop-blur-2xl 
        p-6 rounded-3xl border border-gray-700/30 shadow-2xl
        transform transition-all duration-700
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
        ${isHovered ? 'shadow-yellow-500/10 border-yellow-500/20' : 'shadow-black/20'}
      `}>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center space-x-3 mb-3">
            <div className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full animate-pulse"></div>
            <p className="text-lg font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent uppercase tracking-wider">
              Registration Closes In
            </p>
            <div className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full animate-pulse animation-delay-1000"></div>
          </div>
          
          {/* Urgency Message */}
          <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
            Limited spots available! Join <span className="text-yellow-400 font-semibold">2,347+</span> successful members before time runs out
          </p>
        </div>

        {/* Countdown Grid */}
        <div className="flex justify-center items-center space-x-3 md:space-x-6">
          <TimeUnit value={timeLeft.days} label="Days" delay={0} />
          
          {/* Separator */}
          <div className="flex flex-col space-y-2">
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-amber-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
          </div>
          
          <TimeUnit value={timeLeft.hours} label="Hours" delay={150} />
          
          {/* Separator */}
          <div className="flex flex-col space-y-2">
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-amber-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
          </div>
          
          <TimeUnit value={timeLeft.minutes} label="Minutes" delay={300} />
          
          {/* Separator */}
          <div className="flex flex-col space-y-2">
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-amber-400/50 rounded-full"></div>
            <div className="w-1 h-1 bg-yellow-400/50 rounded-full"></div>
          </div>
          
          <TimeUnit value={timeLeft.seconds} label="Seconds" delay={450} />
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-xs text-gray-400 mb-2">
            <span>Spots Filling Fast</span>
            <span className="text-yellow-400 font-semibold">87% Full</span>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full transition-all duration-1000 ease-out shadow-lg shadow-yellow-500/25 relative overflow-hidden"
              style={{ width: '87%' }}
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 animate-shimmer"></div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-6 text-center">
          <a 
            href="https://www.vumaqash.com/user/register.php?ref=CLEOPHAS"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 group"
          >
            <span className="text-lg">⚡</span>
            <span>Register Now - Limited Time!</span>
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </a>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400/20 rounded-full blur-sm animate-float"></div>
      <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-amber-400/30 rounded-full blur-sm animate-float animation-delay-2000"></div>

      {/* Add custom animations to your CSS */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        .animation-delay-700 {
          animation-delay: 700ms;
        }
        .animation-delay-1000 {
          animation-delay: 1000ms;
        }
        .animation-delay-2000 {
          animation-delay: 2000ms;
        }
      `}</style>
    </div>
  );
};

export default Countdown;